import { ImageSource, Point2D, QuadCorners } from '../types';
import { extractCaptureHints } from './captureMeta';

/**
 * Reads an uploaded file/blob as-is (no resize, no re-encode).
 * Used to preserve the user's original photograph for Storage + restoration.
 * Captures non-sensitive EXIF hints from the ORIGINAL bytes before any canvas re-encode.
 */
export const readOriginalImage = async (
  fileOrBlob: Blob,
  fileName: string
): Promise<ImageSource> => {
  const captureHints = await extractCaptureHints(fileOrBlob);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
      if (!match) {
        reject(new Error('Unexpected image encoding.'));
        return;
      }
      const mimeType = match[1] || fileOrBlob.type || 'image/jpeg';
      const base64Data = match[2];

      const img = new Image();
      img.onload = () => {
        resolve({
          dataUrl,
          base64Data,
          mimeType,
          fileName,
          width: img.naturalWidth || undefined,
          height: img.naturalHeight || undefined,
          captureHints,
        });
      };
      img.onerror = () => {
        // Still preserve bytes even if dimensions cannot be probed
        resolve({
          dataUrl,
          base64Data,
          mimeType,
          fileName,
          captureHints,
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(fileOrBlob);
  });
};

/**
 * Creates a smaller JPEG preview for UI / analysis.
 * Does NOT replace the original — originals are stored unchanged separately.
 */
export const processAndNormalizeImage = async (
  fileOrBlob: Blob,
  fileName: string,
  maxDimension = 1600
): Promise<ImageSource> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.onload = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onerror = () => reject(new Error('Failed to decode image data.'));
      img.onload = () => {
        let width = img.naturalWidth || img.width || 800;
        let height = img.naturalHeight || img.height || 600;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to initialize 2D canvas.'));
          return;
        }

        ctx.fillStyle = '#faf8f5';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const mimeType = 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, 0.88);
        const base64Data = dataUrl.split(',')[1];

        resolve({
          dataUrl,
          base64Data,
          mimeType,
          fileName,
          width,
          height,
        });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(fileOrBlob);
  });
};

/**
 * Rotates an ImageSource by a specified degree (90, 180, 270 degrees clockwise)
 * directly onto a clean canvas using intrinsic natural dimensions.
 */
export const rotateImageSource = async (
  source: ImageSource,
  degrees: number
): Promise<ImageSource> => {
  const normalizedDegrees = ((degrees % 360) + 360) % 360;
  if (normalizedDegrees === 0) return source;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;

        if (!w || !h) {
          reject(new Error('Invalid image dimensions for rotation.'));
          return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not acquire 2D canvas context.'));
          return;
        }

        const isSideways = normalizedDegrees === 90 || normalizedDegrees === 270;
        canvas.width = isSideways ? h : w;
        canvas.height = isSideways ? w : h;

        ctx.fillStyle = '#faf8f5';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalizedDegrees * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2);
        ctx.restore();

        const mimeType = source.mimeType || 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, 0.92);
        const base64Data = dataUrl.split(',')[1];

        resolve({
          dataUrl,
          base64Data,
          mimeType,
          fileName: source.fileName,
          width: canvas.width,
          height: canvas.height,
        });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for rotation.'));
    img.src = source.dataUrl;
  });
};

/**
 * Rotates any image URL string (data URL or blob) by the given degrees.
 * Useful for keeping restored versions in sync when original is rotated.
 */
export const rotateImageUrl = async (imageUrl: string, degrees: number): Promise<string> => {
  const normalizedDegrees = ((degrees % 360) + 360) % 360;
  if (normalizedDegrees === 0) return imageUrl;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          resolve(imageUrl);
          return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(imageUrl);
          return;
        }

        const isSideways = normalizedDegrees === 90 || normalizedDegrees === 270;
        canvas.width = isSideways ? h : w;
        canvas.height = isSideways ? w : h;

        ctx.fillStyle = '#faf8f5';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalizedDegrees * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2);
        ctx.restore();

        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        console.error('Failed to rotate image URL:', err);
        resolve(imageUrl);
      }
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
};

// --- Homography Perspective Warping Solver ---
function solve8x8System(A: number[][], b: number[]): number[] {
  const n = 8;
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) {
        maxRow = k;
      }
    }
    const tempRow = A[i];
    A[i] = A[maxRow];
    A[maxRow] = tempRow;
    const tempB = b[i];
    b[i] = b[maxRow];
    b[maxRow] = tempB;

    if (Math.abs(A[i][i]) < 1e-12) continue;

    for (let k = i + 1; k < n; k++) {
      const c = -A[k][i] / A[i][i];
      for (let j = i; j < n; j++) {
        if (i === j) {
          A[k][j] = 0;
        } else {
          A[k][j] += c * A[i][j];
        }
      }
      b[k] += c * b[i];
    }
  }

  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = 0;
    for (let j = i + 1; j < n; j++) {
      sum += A[i][j] * x[j];
    }
    x[i] = (b[i] - sum) / (A[i][i] || 1e-12);
  }
  return x;
}

function getInverseHomographyMatrix(
  srcPts: { x: number; y: number }[],
  wDst: number,
  hDst: number
): number[] {
  const dstPts = [
    { u: 0, v: 0 },
    { u: wDst, v: 0 },
    { u: wDst, v: hDst },
    { u: 0, v: hDst },
  ];

  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const { u, v } = dstPts[i];
    const { x, y } = srcPts[i];

    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x]);
    b.push(x);

    A.push([0, 0, 0, u, v, 1, -u * y, -v * y]);
    b.push(y);
  }

  const h = solve8x8System(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1.0];
}

/**
 * Unwarps a quadrilateral region defined by 4 corner points into a clean, rectified,
 * straight-on rectangular ImageSource with bilinear interpolation.
 */
export const unwarpPerspectiveCorners = async (
  source: ImageSource,
  corners: QuadCorners
): Promise<ImageSource> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => reject(new Error('Failed to load image for unwarping.'));
    img.onload = () => {
      const srcW = img.naturalWidth || img.width;
      const srcH = img.naturalHeight || img.height;

      const srcPts = [
        { x: corners.topLeft.x * srcW, y: corners.topLeft.y * srcH },
        { x: corners.topRight.x * srcW, y: corners.topRight.y * srcH },
        { x: corners.bottomRight.x * srcW, y: corners.bottomRight.y * srcH },
        { x: corners.bottomLeft.x * srcW, y: corners.bottomLeft.y * srcH },
      ];

      const topDist = Math.hypot(srcPts[1].x - srcPts[0].x, srcPts[1].y - srcPts[0].y);
      const bottomDist = Math.hypot(srcPts[2].x - srcPts[3].x, srcPts[2].y - srcPts[3].y);
      const leftDist = Math.hypot(srcPts[3].x - srcPts[0].x, srcPts[3].y - srcPts[0].y);
      const rightDist = Math.hypot(srcPts[2].x - srcPts[1].x, srcPts[2].y - srcPts[1].y);

      let targetW = Math.max(50, Math.round((topDist + bottomDist) / 2));
      let targetH = Math.max(50, Math.round((leftDist + rightDist) / 2));

      const maxDim = 1200;
      if (targetW > maxDim || targetH > maxDim) {
        if (targetW > targetH) {
          targetH = Math.round((targetH * maxDim) / targetW);
          targetW = maxDim;
        } else {
          targetW = Math.round((targetW * maxDim) / targetH);
          targetH = maxDim;
        }
      }

      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = srcW;
      srcCanvas.height = srcH;
      const srcCtx = srcCanvas.getContext('2d');
      if (!srcCtx) {
        reject(new Error('Failed to get source canvas context.'));
        return;
      }
      srcCtx.drawImage(img, 0, 0);
      const srcImgData = srcCtx.getImageData(0, 0, srcW, srcH);
      const srcData = srcImgData.data;

      const dstCanvas = document.createElement('canvas');
      dstCanvas.width = targetW;
      dstCanvas.height = targetH;
      const dstCtx = dstCanvas.getContext('2d');
      if (!dstCtx) {
        reject(new Error('Failed to get destination canvas context.'));
        return;
      }
      const dstImgData = dstCtx.createImageData(targetW, targetH);
      const dstData = dstImgData.data;

      const H = getInverseHomographyMatrix(srcPts, targetW, targetH);

      for (let v = 0; v < targetH; v++) {
        for (let u = 0; u < targetW; u++) {
          const denom = H[6] * u + H[7] * v + 1.0;
          const sx = (H[0] * u + H[1] * v + H[2]) / denom;
          const sy = (H[3] * u + H[4] * v + H[5]) / denom;

          const dstIdx = (v * targetW + u) * 4;

          if (sx >= 0 && sx < srcW - 1 && sy >= 0 && sy < srcH - 1) {
            const x0 = Math.floor(sx);
            const y0 = Math.floor(sy);
            const x1 = x0 + 1;
            const y1 = y0 + 1;
            const dx = sx - x0;
            const dy = sy - y0;

            const i00 = (y0 * srcW + x0) * 4;
            const i10 = (y0 * srcW + x1) * 4;
            const i01 = (y1 * srcW + x0) * 4;
            const i11 = (y1 * srcW + x1) * 4;

            for (let c = 0; c < 3; c++) {
              const top = srcData[i00 + c] * (1 - dx) + srcData[i10 + c] * dx;
              const bot = srcData[i01 + c] * (1 - dx) + srcData[i11 + c] * dx;
              dstData[dstIdx + c] = Math.round(top * (1 - dy) + bot * dy);
            }
            dstData[dstIdx + 3] = 255;
          } else {
            const cx = Math.max(0, Math.min(srcW - 1, Math.round(sx)));
            const cy = Math.max(0, Math.min(srcH - 1, Math.round(sy)));
            const sIdx = (cy * srcW + cx) * 4;
            dstData[dstIdx] = srcData[sIdx];
            dstData[dstIdx + 1] = srcData[sIdx + 1];
            dstData[dstIdx + 2] = srcData[sIdx + 2];
            dstData[dstIdx + 3] = 255;
          }
        }
      }

      dstCtx.putImageData(dstImgData, 0, 0);

      const mimeType = 'image/jpeg';
      const dataUrl = dstCanvas.toDataURL(mimeType, 0.9);
      const base64Data = dataUrl.split(',')[1];

      resolve({
        dataUrl,
        base64Data,
        mimeType,
        fileName: source.fileName,
        width: targetW,
        height: targetH,
      });
    };
    img.src = source.dataUrl;
  });
};

/**
 * Standard rectangular cropping
 */
export const cropRectangular = async (
  source: ImageSource,
  normRect: { x: number; y: number; width: number; height: number }
): Promise<ImageSource> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => reject(new Error('Failed to load image for crop.'));
    img.onload = () => {
      const srcW = img.naturalWidth || img.width;
      const srcH = img.naturalHeight || img.height;

      const sx = Math.max(0, Math.round(normRect.x * srcW));
      const sy = Math.max(0, Math.round(normRect.y * srcH));
      const sw = Math.min(srcW - sx, Math.max(10, Math.round(normRect.width * srcW)));
      const sh = Math.min(srcH - sy, Math.max(10, Math.round(normRect.height * srcH)));

      const canvas = document.createElement('canvas');
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get crop canvas context.'));
        return;
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

      const mimeType = 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.9);
      const base64Data = dataUrl.split(',')[1];

      resolve({
        dataUrl,
        base64Data,
        mimeType,
        fileName: source.fileName,
        width: sw,
        height: sh,
      });
    };
    img.src = source.dataUrl;
  });
};

/**
 * Generates an authentic archival sample physical photograph directly on canvas.
 */
export const createArchivalSamplePhoto = (sampleId: '1940s-portrait' | '1960s-group'): ImageSource => {
  const canvas = document.createElement('canvas');
  const width = 800;
  const height = sampleId === '1940s-portrait' ? 1000 : 600;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#261e18';
  ctx.fillRect(0, 0, width, height);

  for (let i = 0; i < 40; i++) {
    ctx.strokeStyle = 'rgba(255,255,255,0.02)';
    ctx.lineWidth = Math.random() * 8 + 2;
    ctx.beginPath();
    ctx.moveTo(0, i * (height / 40));
    ctx.lineTo(width, i * (height / 40) + Math.sin(i) * 20);
    ctx.stroke();
  }

  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate(sampleId === '1940s-portrait' ? -0.04 : 0.03);
  const pW = width * 0.82;
  const pH = height * 0.82;
  const px = -pW / 2;
  const py = -pH / 2;

  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetX = 8;
  ctx.shadowOffsetY = 12;

  ctx.fillStyle = sampleId === '1940s-portrait' ? '#ece5d8' : '#e6dfd1';
  ctx.fillRect(px, py, pW, pH);
  ctx.shadowColor = 'transparent';

  const margin = 24;
  const innerW = pW - margin * 2;
  const innerH = pH - margin * 2;
  const ix = px + margin;
  const iy = py + margin;

  if (sampleId === '1940s-portrait') {
    const grad = ctx.createLinearGradient(ix, iy, ix, iy + innerH);
    grad.addColorStop(0, '#534537');
    grad.addColorStop(0.5, '#7d6b56');
    grad.addColorStop(1, '#3b2f24');
    ctx.fillStyle = grad;
    ctx.fillRect(ix, iy, innerW, innerH);

    ctx.fillStyle = '#2b221a';
    ctx.beginPath();
    ctx.ellipse(ix + innerW / 2, iy + innerH * 0.85, innerW * 0.38, innerH * 0.28, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#bda78e';
    ctx.fillRect(ix + innerW / 2 - 25, iy + innerH * 0.52, 50, 45);

    ctx.fillStyle = '#c8b49c';
    ctx.beginPath();
    ctx.ellipse(ix + innerW / 2, iy + innerH * 0.44, 75, 95, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#4f3e30';
    ctx.beginPath();
    ctx.arc(ix + innerW / 2 - 28, iy + innerH * 0.42, 6, 0, Math.PI * 2);
    ctx.arc(ix + innerW / 2 + 28, iy + innerH * 0.42, 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#302419';
    ctx.beginPath();
    ctx.arc(ix + innerW / 2, iy + innerH * 0.36, 85, Math.PI, 0, false);
    ctx.fill();
  } else {
    const grad = ctx.createLinearGradient(ix, iy, ix, iy + innerH);
    grad.addColorStop(0, '#75908b');
    grad.addColorStop(0.5, '#a19f85');
    grad.addColorStop(1, '#5a4f3e');
    ctx.fillStyle = grad;
    ctx.fillRect(ix, iy, innerW, innerH);

    const centers = [ix + innerW * 0.3, ix + innerW * 0.5, ix + innerW * 0.7];
    const colors = ['#7c3b2d', '#2c4d63', '#a37c35'];
    centers.forEach((cx, idx) => {
      ctx.fillStyle = colors[idx];
      ctx.beginPath();
      ctx.ellipse(cx, iy + innerH * 0.78, 65, 90, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#d2b496';
      ctx.beginPath();
      ctx.ellipse(cx, iy + innerH * 0.45, 42, 54, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  ctx.strokeStyle = 'rgba(240, 230, 215, 0.75)';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(ix - 10, iy + innerH * 0.3);
  ctx.lineTo(ix + innerW * 0.65, iy + innerH * 0.55);
  ctx.lineTo(ix + innerW + 10, iy + innerH * 0.48);
  ctx.stroke();

  ctx.lineWidth = 0.8;
  for (let s = 0; s < 12; s++) {
    const sx = ix + Math.random() * innerW;
    const sy = iy + Math.random() * innerH;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx + (Math.random() - 0.5) * 80, sy + (Math.random() - 0.5) * 80);
    ctx.stroke();
  }

  const glare = ctx.createRadialGradient(
    ix + innerW * 0.35,
    iy + innerH * 0.25,
    10,
    ix + innerW * 0.35,
    iy + innerH * 0.25,
    180
  );
  glare.addColorStop(0, 'rgba(255, 255, 255, 0.45)');
  glare.addColorStop(0.5, 'rgba(255, 255, 255, 0.15)');
  glare.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = glare;
  ctx.fillRect(ix, iy, innerW, innerH);

  ctx.fillStyle = '#b78772';
  ctx.beginPath();
  ctx.ellipse(px + 10, py + pH * 0.7, 35, 55, 0.4, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();

  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  const base64Data = dataUrl.split(',')[1];
  return {
    dataUrl,
    base64Data,
    mimeType: 'image/jpeg',
    fileName: `${sampleId}.jpg`,
    width,
    height,
  };
};

/**
 * Convert an in-memory data URL (e.g. RestorationVersion.imageUrl) into ImageSource.
 */
export const imageSourceFromDataUrl = (
  dataUrl: string,
  fileName = "restored.png"
): ImageSource => {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    throw new Error("Expected a data URL image.");
  }
  return {
    dataUrl,
    mimeType: match[1] || "image/png",
    base64Data: match[2],
    fileName,
  };
};

const DEFAULT_WATERMARK_ASSET = "/brand/kinvera-watermark.png";

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export type WatermarkOptions = {
  /** Watermark width as a fraction of image width (default 0.15). */
  widthRatio?: number;
  /** Margin as a fraction of the shorter image side (default 0.025). */
  marginRatio?: number;
  watermarkUrl?: string;
};

/**
 * Produce a watermarked DERIVATIVE at the source image's native resolution.
 * Does not mutate the master. On watermark asset failure, returns the original URL.
 */
export const createWatermarkedImage = async (
  sourceImageUrl: string,
  options: WatermarkOptions = {}
): Promise<string> => {
  const widthRatio = options.widthRatio ?? 0.15;
  const marginRatio = options.marginRatio ?? 0.025;
  const watermarkUrl = options.watermarkUrl || DEFAULT_WATERMARK_ASSET;

  try {
    const [source, mark] = await Promise.all([
      loadImageElement(sourceImageUrl),
      loadImageElement(watermarkUrl),
    ]);

    const w = source.naturalWidth || source.width;
    const h = source.naturalHeight || source.height;
    if (!w || !h) return sourceImageUrl;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return sourceImageUrl;

    ctx.drawImage(source, 0, 0, w, h);

    const markNatW = mark.naturalWidth || mark.width || 1;
    const markNatH = mark.naturalHeight || mark.height || 1;
    const targetW = Math.max(48, Math.min(Math.round(w * widthRatio), Math.round(w * 0.22)));
    const targetH = Math.round((targetW * markNatH) / markNatW);
    const margin = Math.max(8, Math.round(Math.min(w, h) * marginRatio));
    const dx = w - targetW - margin;
    const dy = h - targetH - margin;

    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
    ctx.shadowBlur = Math.max(4, Math.round(targetW * 0.06));
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(1, Math.round(targetH * 0.04));
    ctx.globalAlpha = 0.92;
    ctx.drawImage(mark, dx, dy, targetW, targetH);
    ctx.restore();

    const mime =
      sourceImageUrl.startsWith("data:image/jpeg") || sourceImageUrl.startsWith("data:image/jpg")
        ? "image/jpeg"
        : "image/png";
    return mime === "image/jpeg" ? canvas.toDataURL(mime, 0.92) : canvas.toDataURL("image/png");
  } catch (err) {
    console.warn("Watermark skipped:", err);
    return sourceImageUrl;
  }
};

/**
 * ANALYSIS-ONLY numbered subject map overlaid on the forensic preview.
 * Never used as restoration input / export / watermark source.
 * Badges are centered on each face box (for Gemini), not at the top-left corner.
 */
export const createAnnotatedSubjectMap = async (
  source: ImageSource,
  faces: Array<{
    index: number;
    faceBounds: { xMin: number; yMin: number; xMax: number; yMax: number };
  }>
): Promise<ImageSource> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => reject(new Error('Failed to load preview for subject map annotation.'));
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) {
        reject(new Error('Invalid preview dimensions for subject map.'));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not acquire canvas for subject map.'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      const badgeR = Math.max(10, Math.round(Math.min(w, h) * 0.018));
      for (const face of faces) {
        const { xMin, yMin, xMax, yMax } = face.faceBounds;
        const fx = xMin * w;
        const fy = yMin * h;
        const fw = Math.max(1, (xMax - xMin) * w);
        const fh = Math.max(1, (yMax - yMin) * h);
        const cx = fx + fw / 2;
        const cy = fy + fh / 2;

        ctx.strokeStyle = 'rgba(25, 170, 168, 0.55)';
        ctx.lineWidth = Math.max(2, Math.round(Math.min(fw, fh) * 0.04));
        ctx.strokeRect(fx, fy, fw, fh);

        ctx.beginPath();
        ctx.arc(cx, cy, badgeR, 0, Math.PI * 2);
        ctx.fillStyle = '#19aaa8';
        ctx.fill();
        ctx.lineWidth = Math.max(2, badgeR * 0.22);
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();

        ctx.fillStyle = '#0a1c2b';
        const fontPx = Math.round(badgeR * 1.15);
        ctx.font = 'bold ' + fontPx + 'px Spline Sans, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(face.index), cx, cy + 0.5);
      }

      const mimeType = 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.9);
      const base64Data = dataUrl.split(',')[1];
      resolve({
        dataUrl,
        base64Data,
        mimeType,
        fileName: 'subject-map-' + (source.fileName || 'preview') + '.jpg',
        width: w,
        height: h,
      });
    };
    img.src = source.dataUrl;
  });
};

/**
 * UI-only face thumbnail cropped from the analysis preview.
 * Adds padding around the detector box so hair/context remain visible.
 * Not persisted to Storage/Firestore.
 */
export const createFaceThumbnail = async (
  imageUrl: string,
  faceBounds: { xMin: number; yMin: number; xMax: number; yMax: number },
  options?: { padding?: number; size?: number }
): Promise<string | null> => {
  const padding = options?.padding ?? 0.12;
  const outSize = options?.size ?? 112;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) {
        resolve(null);
        return;
      }

      const boxW = Math.max(0.02, faceBounds.xMax - faceBounds.xMin);
      const boxH = Math.max(0.02, faceBounds.yMax - faceBounds.yMin);
      const cx = (faceBounds.xMin + faceBounds.xMax) / 2;
      const cy = (faceBounds.yMin + faceBounds.yMax) / 2;
      const side = Math.max(boxW, boxH) * (1 + padding * 2);
      let x0 = cx - side / 2;
      let y0 = cy - side / 2;
      // Mild portrait bias: shift crop slightly upward to include hair
      y0 -= side * 0.06;
      x0 = Math.max(0, Math.min(1 - side, x0));
      y0 = Math.max(0, Math.min(1 - side, y0));
      const sideClamped = Math.min(side, 1 - x0, 1 - y0);

      const sx = Math.floor(x0 * w);
      const sy = Math.floor(y0 * h);
      const sw = Math.max(1, Math.floor(sideClamped * w));
      const sh = Math.max(1, Math.floor(sideClamped * h));

      const canvas = document.createElement('canvas');
      canvas.width = outSize;
      canvas.height = outSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outSize, outSize);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    img.src = imageUrl;
  });
};
